import type { VehicleInsurance, VehicleInsuranceSaveRequestV2 } from "@credit-acceptance/step6-bff-client";

import { isRealIsoDate } from "./ContractVehicleInformation.schema";

/** A fully-zeroed phone number is the legacy placeholder — treated the same as blank. */
const BLANK_PHONE_PLACEHOLDER = "000-000-0000";

export const FULL_COVERAGE_VALUES = ["Y", "N"] as const;
export type FullCoverageValue = (typeof FULL_COVERAGE_VALUES)[number];

export const AGENT_NAME_MAX_LENGTH = 40;
export const DEDUCTIBLE_MAX_LENGTH = 7;
export const INSURER_NAME_MAX_LENGTH = 100;
const PHONE_PATTERN = /^\d{3}-\d{3}-\d{4}$/;

/**
 *
 * @param value The formatted phone value.
 * @returns {boolean} Whether the phone can be dialed.
 */
export function isValidInsurancePhone(value: string): boolean {
  return PHONE_PATTERN.test(value) && value !== BLANK_PHONE_PLACEHOLDER;
}

/**
 *
 * @param value The entered phone value.
 * @returns {string} A formatted US phone, or the original partial/invalid value.
 */
export function formatInsurancePhone(value: string): string {
  const match = /^(?:1[\s.-]?)?\(?(\d{3})\)?[\s.-]?(\d{3})[\s.-]?(\d{4})$/.exec(value.trim());
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const local = /^(\d{3})[\s.-]?(\d{4})$/.exec(value.trim());
  return local ? `${local[1]}-${local[2]}` : value;
}

export interface InsuranceInformationInput {
  fullCoverage?: FullCoverageValue;
  insurerName: string;
  insurerPhone: string;
  agentName: string;
  expirationDate: string;
  // RHF needs null rather than undefined to clear a field with a saved default.
  comprehensiveDeductibleAmount?: number | null;
  collisionDeductibleAmount?: number | null;
  isCaLossPayee?: VehicleInsurance["isCaLossPayee"];
}

/**
 * Builds RHF defaultValues from the real `VehicleInsurance` prop.
 * @param insurance The dashboard response's `VehicleInsurance`, if any.
 * @returns {InsuranceInformationInput} Seed values for `useForm`'s `defaultValues`.
 */
export function buildInsuranceDefaultValues(insurance?: VehicleInsurance): InsuranceInformationInput {
  let fullCoverage: FullCoverageValue | undefined;
  if (typeof insurance?.fullCoverage === "boolean") {
    fullCoverage = insurance.fullCoverage ? "Y" : "N";
  }
  return {
    fullCoverage,
    insurerName: insurance?.insurerName?.toUpperCase() ?? "",
    insurerPhone: insurance?.insurerPhone === BLANK_PHONE_PLACEHOLDER ? "" : (insurance?.insurerPhone ?? ""),
    agentName: insurance?.agentName ?? "",
    expirationDate: insurance?.expirationDate ?? "",
    // eslint-disable-next-line unicorn/no-null -- `null` is the blank sentinel for these fields, see the schema above
    comprehensiveDeductibleAmount: insurance?.comprehensiveDeductibleAmount ?? null,
    // eslint-disable-next-line unicorn/no-null -- see above
    collisionDeductibleAmount: insurance?.collisionDeductibleAmount ?? null,
    isCaLossPayee: insurance?.isCaLossPayee ?? "U",
  };
}

/**
 *
 * @param amount The deductible or its blank sentinel.
 * @returns {string} The save representation, including explicit clears.
 */
function deductibleText(amount?: number | null): string {
  return typeof amount === "number" ? String(amount) : "";
}

/**
 * Maps the insurance form to the save request's `vehicleInsurance` leg.
 * Blank strings explicitly clear previously saved values; an invalid date is omitted
 * because the endpoint expects a date, not raw input. Full Coverage No clears both deductibles.
 * @param values Current form values.
 * @returns {VehicleInsuranceSaveRequestV2} The insurance-leg save payload.
 */
export function toInsuranceSaveRequest(values: InsuranceInformationInput): VehicleInsuranceSaveRequestV2 {
  const phone = formatInsurancePhone(values.insurerPhone);
  const hasDeductibles = values.fullCoverage !== "N";
  const request: VehicleInsuranceSaveRequestV2 = {
    insurerName: values.insurerName.trim().toUpperCase(),
    insurerPhone: phone === BLANK_PHONE_PLACEHOLDER ? "" : phone,
    agent: values.agentName.toUpperCase(),
    effectiveDate: !values.expirationDate || isRealIsoDate(values.expirationDate) ? values.expirationDate : undefined,
    fullCoverage: values.fullCoverage === undefined ? undefined : values.fullCoverage === "Y",
    compDeductible: hasDeductibles ? deductibleText(values.comprehensiveDeductibleAmount) : "",
    collisionDeductible: hasDeductibles ? deductibleText(values.collisionDeductibleAmount) : "",
    selectedCALossPayee: values.isCaLossPayee,
  };
  return request;
}
